// External Imports
import { createSelector } from 'reselect';
import { compile } from 'path-to-regexp';

// Internal Imports
import { editionPathTypeOrder, HOME } from '../config/types/path.js';
import { checkUsEnglish } from '../utils/checkUsEnglish.js';
import {
  getLanguageCodeFromPath,
  getLinkParts,
  isAbsolute,
  isBabbelHostname,
  prependEditionHostname,
  hostIsStaging,
} from '../utils/url.js';
import { getFirstMatch } from './getFirstMatch.js';
import { getEditionFromLocale } from './getEdition.js';
import { getMatch } from './getMatch.js';
import { getPathMap } from './getPathMap.js';

// Local Functions
const createGetEditionPath = (link) =>
  createSelector([getPathMap, getMatch], (pathMap, { params }) => {
    const linkParts = getLinkParts(link);
    const linkQuery = linkParts.query || '';
    const isHttpLink = /^https?:$/.test(linkParts.protocol); // For example: email links are non-HTTP
    const isAbsoluteLink = isAbsolute(link);
    const { hostname } = linkParts;
    const isAbsoluteNonBabbelLink =
      isAbsoluteLink && !isBabbelHostname(hostname) && !hostIsStaging(hostname);

    if (!isHttpLink || isAbsoluteNonBabbelLink) {
      return link;
    }

    const { pathname } = linkParts;

    const editionMatch = getFirstMatch(editionPathTypeOrder, pathMap, pathname);

    if (!editionMatch) {
      return isAbsoluteLink ? link : prependEditionHostname(params.language, pathname);
    }

    const pathLanguageCode = getLanguageCodeFromPath(pathname);
    const newLanguageCode =
      HOME === editionMatch.pathType
        ? checkUsEnglish(pathLanguageCode)
        : getEditionFromLocale(pathLanguageCode);
    const pathTypeToPath = compile(pathMap[editionMatch.pathType]);
    const convertedPath = pathTypeToPath({
      ...editionMatch.params,
      language: newLanguageCode,
    });
    const fullLink = prependEditionHostname(newLanguageCode, convertedPath) + linkQuery;
    return fullLink;
  });

// Module Exports
export { createGetEditionPath };
